/**
 * Provider for the multi-kernel pool.
 *
 * Replaces the single-kernel JupyterProvider with a pool-based model
 * where multiple consumers (Code Interface, Antioch Agent) can each
 * independently start, attach to, and detach from kernels.
 */

import * as React from 'react';
// Import directly from the CodeMirror-free module, NOT the '@/lib/jupyter'
// barrel: this provider is mounted app-wide, and the barrel evaluates
// codeIntelligence (which top-level-imports @codemirror/state and
// @codemirror/view), pulling the editor libs into the initial bundle for users
// who never open the code pane.
import { clearInspectCache } from '@/lib/jupyter/inspectCache';
import type { KernelInfo, KernelSpecInfo } from '@/lib/jupyter/KernelPool';
import { KernelPool } from '@/lib/jupyter/KernelPool';
import { getLocalJupyterUrl } from '@/lib/utils/workspace';
import { useServiceTokens } from './ServiceTokensProvider';
import { useWorkspace } from './WorkspaceProvider';

interface KernelPoolContextValue {
  pool: KernelPool | null;
  kernels: KernelInfo[];
  /** Kernelspecs registered on the workspace's Jupyter server. */
  kernelSpecs: KernelSpecInfo[];
  /** Name of the server's default kernelspec, or null until fetched. */
  defaultKernelSpec: string | null;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
}

const KernelPoolContext = React.createContext<KernelPoolContextValue | null>(null);

export function KernelPoolProvider({ children }: { children: React.ReactNode }) {
  const { connectedWorkspace, streamTarget, isLocalDev, localWorkspaceId } = useWorkspace();
  const { tokens: serviceTokens, refreshTokens } = useServiceTokens();

  const [pool, setPool] = React.useState<KernelPool | null>(null);
  const [kernels, setKernels] = React.useState<KernelInfo[]>([]);
  const [kernelSpecs, setKernelSpecs] = React.useState<KernelSpecInfo[]>([]);
  const [defaultKernelSpec, setDefaultKernelSpec] = React.useState<string | null>(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const poolRef = React.useRef<KernelPool | null>(null);

  // Derive Jupyter URL
  const jupyterUrl = React.useMemo(() => {
    if (!streamTarget || !connectedWorkspace) return null;
    if (isLocalDev && connectedWorkspace.id === localWorkspaceId) {
      return getLocalJupyterUrl();
    }
    return `${streamTarget.httpBaseUrl.replace(/\/$/, '')}/jupyter`;
  }, [streamTarget, connectedWorkspace, isLocalDev, localWorkspaceId]);

  const workspaceToken = React.useMemo(() => {
    if (isLocalDev && connectedWorkspace?.id === localWorkspaceId) return undefined;
    return serviceTokens?.workspace_token;
  }, [isLocalDev, localWorkspaceId, connectedWorkspace?.id, serviceTokens?.workspace_token]);
  const tokenRequired = !(isLocalDev && connectedWorkspace?.id === localWorkspaceId);
  const jupyterAuthReady = !tokenRequired || workspaceToken !== undefined;

  // Propagate token refreshes to the existing pool so Jupyter API
  // calls use the latest credential (prevents 401s after token expiry)
  React.useEffect(() => {
    if (poolRef.current) {
      poolRef.current.updateToken(workspaceToken);
    }
  }, [workspaceToken]);

  // Callback the pool invokes when a Jupyter API call returns 401.
  // Refreshes tokens through the shared provider and returns the fresh workspace token.
  const handleTokenRefreshNeeded = React.useCallback(async (): Promise<string | undefined> => {
    const freshTokens = await refreshTokens();
    return freshTokens?.workspace_token;
  }, [refreshTokens]);

  // Register the token refresh callback on the pool
  React.useEffect(() => {
    if (poolRef.current) {
      poolRef.current.setTokenRefreshCallback(handleTokenRefreshNeeded);
    }
  }, [handleTokenRefreshNeeded]);

  const connect = React.useCallback(async () => {
    if (!jupyterUrl || !jupyterAuthReady || poolRef.current) return;

    const newPool = new KernelPool(jupyterUrl, workspaceToken);
    newPool.setTokenRefreshCallback(handleTokenRefreshNeeded);
    poolRef.current = newPool;
    setPool(newPool);
    setIsConnected(true);

    newPool.onKernelsChanged((updated) => {
      setKernels(updated);
    });

    await newPool.refresh();

    // Kernelspecs are static for a workspace image; fetch once on connect so
    // the kernel selector can offer every registered spec. Best-effort: the
    // pool retains its cache and resolves to a (possibly empty) list on
    // failure, so a kernelspec lookup error never blocks the connection.
    const specs = await newPool.refreshKernelSpecs();
    if (poolRef.current === newPool) {
      setKernelSpecs(specs);
      setDefaultKernelSpec(newPool.defaultKernelSpec);
    }
  }, [jupyterUrl, jupyterAuthReady, workspaceToken, handleTokenRefreshNeeded]);

  const disconnect = React.useCallback(() => {
    if (poolRef.current) {
      poolRef.current.dispose();
      poolRef.current = null;
    }
    // The code-intelligence inspect cache holds per-kernel introspection
    // (hover docstrings / signatures) keyed only by identifier. Clear it on
    // teardown so a workspace switch can't serve the previous workspace's
    // kernel docs for a same-named symbol until the TTL lapses.
    clearInspectCache();
    setPool(null);
    setKernels([]);
    setKernelSpecs([]);
    setDefaultKernelSpec(null);
    setIsConnected(false);
  }, []);

  // Disconnect when workspace changes (must be declared before auto-connect
  // so React fires it first — clears poolRef before auto-connect checks it)
  const prevWorkspaceId = React.useRef<string | null>(null);
  React.useEffect(() => {
    const currentId = connectedWorkspace?.id ?? null;
    if (prevWorkspaceId.current !== null && prevWorkspaceId.current !== currentId) {
      disconnect();
    }
    prevWorkspaceId.current = currentId;
  }, [connectedWorkspace?.id, disconnect]);

  // Auto-connect when workspace and token are available
  React.useEffect(() => {
    if (jupyterUrl && jupyterAuthReady && !poolRef.current) {
      connect();
    }
  }, [jupyterUrl, jupyterAuthReady, connect]);

  // Clean up on unmount. Clear the inspect cache too (mirroring
  // ``disconnect``) so a remounted provider can't serve the previous
  // kernel's docstrings for a same-named symbol until the TTL lapses.
  React.useEffect(() => {
    return () => {
      if (poolRef.current) {
        poolRef.current.dispose();
      }
      clearInspectCache();
    };
  }, []);

  const value = React.useMemo(
    () => ({ pool, kernels, kernelSpecs, defaultKernelSpec, isConnected, connect, disconnect }),
    [pool, kernels, kernelSpecs, defaultKernelSpec, isConnected, connect, disconnect]
  );

  return <KernelPoolContext.Provider value={value}>{children}</KernelPoolContext.Provider>;
}

export function useKernelPool(): KernelPoolContextValue {
  const context = React.useContext(KernelPoolContext);
  if (!context) {
    throw new Error('useKernelPool must be used within a KernelPoolProvider');
  }
  return context;
}
